import SpinLoading from '../spinLoading'
import { Upload } from 'antd'
import { Image } from 'antd'
import type { RcFile, UploadProps } from 'antd/es/upload'
import type { UploadFile } from 'antd/es/upload/interface'
import { IImageUpload } from 'interfaces'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFileService } from 'services'
import styled from 'styled-components'

const StyledUpload = styled(Upload)`
  .ant-upload-select-picture-card {
    width: 100%;
    background: var(--color-gray-102);
    border: none;
    height: 50px;
  }
  .ant-upload-list-item {
    border-color: var(--color-primary-108);
  }
  .ant-tooltip-content {
    display: none;
  }
  .ant-upload-select-picture-card {
    margin: 0px;
  }
`
const StyledLabel = styled.div`
  color: var(--color-gray-111);
  font-weight: 600;
  font-size: 1.4rem;
  margin-bottom: 6px;
  span {
    color: var(--color-red-107);
  }
`
const StyledContainer = styled.div`
  svg {
    width: 20px;
    height: 20px;
  }
  &.prevent-remove {
    .ant-btn[title='Remove file'] {
      display: none;
    }
    .ant-upload {
      pointer-events: none;
    }
  }
  &.disabled {
    opacity: 0.5;
  }
  &.disabled .ant-upload-select {
    cursor: not-allowed;
    .ant-upload {
      pointer-events: none;
    }
  }
  .ant-upload {
    border-radius: 4px;
    background: var(--color-gray-102);
  }
`

export interface IUploadImageProps {
  required?: boolean
  onChange?: (url: string, img?: IImageUpload) => void
  onRemove?: (id?: number) => void
  value?: string
  buttonUpload?: ReactNode
  preventRemove?: boolean
  disabled?: boolean
  listImage?: any
  className?: string
  title?: string | ReactNode
  hideUpload?: boolean
}
const UploadImage = ({
  required,
  onChange,
  onRemove,
  value,
  buttonUpload,
  preventRemove,
  title,
  disabled,
  className,
  hideUpload,
  listImage
}: IUploadImageProps) => {
  const { postFilesToServer } = useFileService()
  const [previewOpen, setPreviewOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  // const [, setPreviewImage] = useState(value)
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0)
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const { t } = useTranslation()
  const fileReaderRef = useRef<any>(new FileReader())
  const imageRef = useRef<IImageUpload | null>(null)

  const getBase64 = (file: RcFile): Promise<string> => {
    if (fileReaderRef.current) {
      fileReaderRef?.current?.abort()
    }
    return new Promise((resolve, reject) => {
      if (!file) return undefined
      const reader = fileReaderRef.current
      reader?.readAsDataURL(file)
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = (error: any) => reject(error)
    })
  }

  useEffect(() => {
    if (value && fileList.length === 0) {
      setFileList([
        {
          uid: value,
          name: 'image-sub-service',
          thumbUrl: value
        } as UploadFile
      ])
    }
    return () => {
      if (fileReaderRef.current) {
        fileReaderRef?.current?.abort()
      }
    }
  }, [value])

  const handleCancel = () => {
    setPreviewOpen(false)
    const item: any = document.getElementsByClassName(
      'ant-image-preview-wrap'
    )?.[0]
    item.style.display = 'none'
  }

  const handleOnRemove = () => {
    if (onRemove) onRemove(imageRef?.current?.id)
  }

  const handlePreview = async (file: UploadFile, index: number) => {
    setSelectedImageIndex(index)
    setPreviewOpen(true)
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file?.originFileObj as RcFile)
    }

    // setPreviewImage(file.url || (file.preview as string))
    setPreviewOpen(true)
    // setPreviewTitle(
    //   file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1)
  }

  const handleChange: UploadProps['onChange'] = async ({
    fileList: newFileList
  }) => {
    setFileList(newFileList)
    if (newFileList?.length === 0 && onChange) {
      onChange(undefined as any)
      return
    }
    const file = await getBase64(newFileList[0]?.originFileObj as RcFile)
    setLoading(true)
    postFilesToServer([
      {
        type: 0,
        mime_type: newFileList[0].type || '',
        size: newFileList[0].size || 0,
        data: file?.split(',')[1]
      }
    ])
      .then((res) => {
        imageRef.current = res?.data?.[0] as IImageUpload
        if (onChange) onChange(res?.data?.[0]?.url, res?.data?.[0])
        // setPreviewImage(res?.data?.[0])
      })
      .finally(() => setLoading(false))
  }
  return (
    <SpinLoading loading={loading}>
      <StyledContainer
        className={`upload-image ${preventRemove ? 'prevent-remove' : ''} ${
          disabled ? 'disabled' : ''
        } ${className}`}>
        {title && (
          <StyledLabel>
            {title !== undefined ? title : t('attachment-img')}
            {required && <span> *</span>}
          </StyledLabel>
        )}
        <StyledUpload
          listType="picture-card"
          fileList={fileList}
          accept=".png, .jpg, .jpeg"
          multiple={false}
          beforeUpload={() => false}
          onRemove={handleOnRemove}
          onPreview={(file) =>
            handlePreview(file, listImage.indexOf(file?.uid))
          }
          onChange={handleChange}>
          {fileList?.length >= 1 || hideUpload
            ? null
            : buttonUpload || t('description-dagger-drop-img')}
        </StyledUpload>

        {previewOpen && (
          <Image.PreviewGroup
            preview={{
              visible: previewOpen,
              onVisibleChange: () => handleCancel(),
              current: selectedImageIndex,
              rootClassName: previewOpen ? 'upload-image-preview' : ''
            }}>
            {listImage.map((data: any, index: number) => {
              return <Image key={index} src={data} />
            })}
          </Image.PreviewGroup>
        )}
      </StyledContainer>
    </SpinLoading>
  )
}

export default UploadImage
